import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { Divider } from "../../../../../components/Divider";
import Spacer from "../../../../../components/Spacer";
import {
	CheckboxGroup,
	SectionLayout,
	SettingsFormFooter,
	SettingsToggle,
	UnsavedChangesTracker,
} from "../../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";
import useUnsavedChanges from "../../../../../hooks/useUnsavedChanges";
import { useAiSettingsDefaults } from "../../hooks";

// Extract keys dynamically from the schemas to stay in sync with schema changes
const getSchemaKeys = <T extends z.ZodObject<z.ZodRawShape>>(schema: T): Array<keyof z.infer<T>> => {
	return Object.keys(schema.shape) as Array<keyof z.infer<T>>;
};

const AIInsightsMeetingsSettingsTab = () => {
	const { value: isPreMeetingPrepEnabled } = useFeatureFlagQuery("ff-temp-is-ai-pre-meeting-prep-enabled", false);

	const formSchema = AiSettingsSchema.shape.meetingWorkspaceInsightsSettings.extend(
		AiSettingsSchema.shape.aiCoachingSettings.shape
	);
	const formRef = useRef<FormHandle<typeof formSchema>>(null);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// Unsaved changes tracking
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// toggles
	const isMeetingWorkspaceInsightsAssistantEnabled = data?.meetingWorkspaceInsightsEnabled ?? true;
	const isAiCoachingEnabled = data?.aiCoachingEnabled ?? false;

	// toggle handlers
	const handleMeetingWorkspaceInsightsAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ meetingWorkspaceInsightsEnabled: val });
		toast.success(`Meeting Workspace Insights Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleAiCoachingToggle = async (val: boolean) => {
		await updateAiSettings({ aiCoachingEnabled: val });
		toast.success(`Pre-Meeting Preparation ${val === true ? "Enabled" : "Disabled"}.`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({ value, resetForm }: { value: z.infer<typeof formSchema>; resetForm: () => void }) => {
		try {
			// Reconstruct nested structure from flat form value
			const meetingWorkspaceInsightsSettingsKeys = getSchemaKeys(
				AiSettingsSchema.shape.meetingWorkspaceInsightsSettings
			);
			const aiCoachingSettingsKeys = getSchemaKeys(AiSettingsSchema.shape.aiCoachingSettings);

			const meetingWorkspaceInsightsSettings = Object.fromEntries(
				meetingWorkspaceInsightsSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.meetingWorkspaceInsightsSettings>;

			const aiCoachingSettings = Object.fromEntries(
				aiCoachingSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.aiCoachingSettings>;

			await updateAiSettings({ meetingWorkspaceInsightsSettings, aiCoachingSettings });
			toast.success("Your changes have been saved.");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings.");
		}
	};

	// Checkbox options for Meeting Workspace Insights
	const checkBoxOptions = [
		{ value: "progressAndGrowth", label: "Progress & Growth", dataTestId: "progress-and-growth-checkbox" },
		{
			value: "keyAccomplishmentsAndWins",
			label: "Key Accomplishments & Wins",
			dataTestId: "key-accomplishments-and-wins-checkbox",
		},
		{
			value: "developmentAreasAndGrowthOpportunities",
			label: "Development Areas & Growth Opportunities",
			dataTestId: "development-areas-and-growth-opportunities-checkbox",
		},
		{
			value: "notableDiscussionsAndDecisions",
			label: "Notable Discussions & Decisions",
			dataTestId: "notable-discussions-and-decisions-checkbox",
		},
		{ value: "actionItemsAnalysis", label: "Action Item Analysis", dataTestId: "action-items-analysis-checkbox" },
	];

	// Checkbox options for AI Coaching
	const aiCoachingCheckboxOptions = [
		{
			value: "suggestedAgendaItems",
			label: "Suggested Agenda Items",
			dataTestId: "suggested-agenda-items-checkbox",
		},
		{
			value: "goalsForDiscussion",
			label: "Goals For Discussion",
			dataTestId: "goals-for-discussion-checkbox",
		},
		{ value: "recentFeedback", label: "Recent Feedback", dataTestId: "recent-feedback-checkbox" },
		{ value: "recentReviews", label: "Recent Reviews", dataTestId: "recent-reviews-checkbox" },
		{ value: "surveyInsights", label: "Survey Insights", dataTestId: "survey-insights-checkbox" },
	];

	const defaultValues = useAiSettingsDefaults({
		schema: formSchema,
		data,
		extractDefaults: (allDefaults) => ({
			...allDefaults.meetingWorkspaceInsightsSettings,
			...allDefaults.aiCoachingSettings,
		}),
		extractData: (data) => ({
			...data.meetingWorkspaceInsightsSettings,
			...data.aiCoachingSettings,
		}),
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="ai-insights-meetings-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={formSchema}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="ai-insights-meetings-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>
			<SectionLayout
				key="meetings-settings-section"
				title="Meeting Workspace Insights Assistant"
				description="The Meeting Workspace Insights Assistant allows participants and admins to generate insights about all the meetings within a meeting workspace."
			>
				<SettingsToggle
					key="meeting-workspace-insights-toggle"
					id="enable-meeting-workspace-insights-assistant"
					label="Enable Meeting Workspace Insights Assistant"
					description="Disabling this setting will hide the ability for participants and admins to generate insights for a meeting workspace."
					checked={isMeetingWorkspaceInsightsAssistantEnabled}
					onCheckedChange={handleMeetingWorkspaceInsightsAssistantToggle}
					dataTestId="meeting-workspace-insights-toggle"
				/>

				<CheckboxGroup
					key="meetings-insights-content-checkboxes"
					title="Insights Content"
					description="Control each section of content inside your meeting workspace insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the meeting workspace."
					options={checkBoxOptions}
				/>
			</SectionLayout>

			{isPreMeetingPrepEnabled && (
				<>
					<Spacer key="spacer-1" height={5} />
					<Divider key="divider" />
					<Spacer key="spacer-2" height={4} />

					<SectionLayout
						key="ai-coaching-section"
						title="AI Coaching"
						description="The AI Coaching Assistant helps managers prepare for upcoming meetings by generating actionable insights, agenda items, and discussion topics from recent meetings, employee goals, performance reviews, survey results, and feedback."
					>
						<SettingsToggle
							key="ai-coaching-toggle"
							id="enable-pre-meeting-preparation"
							label="Enable Pre-Meeting Preparation"
							description="Disabling this setting will hide the ability for managers to generate actionable insights, agenda items, and discussion topics for an upcoming meeting."
							checked={isAiCoachingEnabled}
							onCheckedChange={handleAiCoachingToggle}
							dataTestId="ai-coaching-toggle"
						/>

						<CheckboxGroup
							key="ai-coaching-content-checkboxes"
							title="Pre-Meeting Preparation Content"
							description="Control each section provided by the AI Coach, to only highlight content that is relevant to you and your company. These options are in addition to sections for current meeting context and a recap of the previous 3 meetings."
							options={aiCoachingCheckboxOptions}
						/>
					</SectionLayout>
				</>
			)}

			<SettingsFormFooter
				key="meetings-settings-footer"
				formId="ai-insights-meetings-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default AIInsightsMeetingsSettingsTab;
